import { useEffect, useState, useLayoutEffect, useCallback } from "react";
import { X, ArrowRight, Sparkles } from "lucide-react";
import { useLang } from "@/lib/lang-context";

const STORAGE_KEY = "dr_onboarding_v1";

/**
 * First-visit onboarding walkthrough.
 *
 * Highlights the four key elements (URL tab, free-text tab, analyse button,
 * result area) with a backdrop + spotlight + tooltip card. The flag in
 * localStorage prevents re-showing for returning visitors. Use the
 * `?tour=1` query param to force re-open for screenshots / QA.
 *
 * No external dependency on purpose — Driver.js / Intro.js add 12–25KB and
 * we only need a 4-step linear flow. Custom keeps it ~120 LoC.
 */
const STEPS_IT = [
  {
    selector: '[data-testid="tab-url"]',
    title: "Incolla un link",
    body: "Copia l'URL di un annuncio immobiliare trovato sul web e incollalo qui. Riceverai un report completo: punteggio affare, rendita stimata, rischi normativi.",
  },
];

const STEPS_EN = [
  {
    selector: '[data-testid="tab-url"]',
    title: "Paste a listing link",
    body: "Copy the URL of any real-estate listing you found online and paste it here. You'll get a full report: deal score, rental yield estimate, regulatory risks.",
  },
];

const OnboardingTour = () => {
  const { lang } = useLang();
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const [open, setOpen] = useState(false);

  const steps = lang === "en" ? STEPS_EN : STEPS_IT;
  const current = steps[step];

  // Decide whether to open on mount. Skips the tour for returning visitors,
  // honours `?tour=1` for forced reopens (handy for screenshots).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const forced = new URLSearchParams(window.location.search).get("tour") === "1";
    const seen = window.localStorage.getItem(STORAGE_KEY);
    if (forced || !seen) {
      // Defer one tick so the Hero / InputPanel are mounted and measurable.
      const id = setTimeout(() => setOpen(true), 400);
      return () => clearTimeout(id);
    }
  }, []);

  // Recompute the spotlight rect whenever the step changes or the user resizes.
  const measure = useCallback(() => {
    if (!open || !current) return;
    const el = document.querySelector(current.selector);
    if (!el) {
      // The element isn't mounted yet (e.g. tab not visible). Skip the step
      // gracefully so the tour never gets stuck on a missing selector.
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setRect({
      top: r.top + window.scrollY,
      left: r.left + window.scrollX,
      width: r.width,
      height: r.height,
    });
    // Scroll the highlighted element into view so the tooltip never falls
    // below the fold on long pages or short viewports.
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [open, current]);

  useLayoutEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  const finish = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, "1");
    } catch {}
    setOpen(false);
  };

  if (!open || !current) return null;

  // Compute tooltip position: below the highlighted element by default,
  // or above when there isn't enough room.
  const pad = 12;
  const tooltipMaxWidth = 320;
  let tooltipStyle = {};
  if (rect) {
    const below = rect.top + rect.height + pad;
    const enoughRoomBelow = below + 220 < window.scrollY + window.innerHeight;
    tooltipStyle = {
      top: enoughRoomBelow ? below : rect.top - 220 - pad,
      left: Math.max(
        16,
        Math.min(
          window.innerWidth - tooltipMaxWidth - 16,
          rect.left + rect.width / 2 - tooltipMaxWidth / 2
        )
      ),
      width: tooltipMaxWidth,
    };
  } else {
    // No measurable target — fall back to a centred card so the user can
    // still skip / next without dead-ending the flow.
    tooltipStyle = {
      top: window.scrollY + window.innerHeight / 2 - 110,
      left: window.innerWidth / 2 - tooltipMaxWidth / 2,
      width: tooltipMaxWidth,
    };
  }

  return (
    <div
      data-testid="onboarding-tour"
      className="fixed inset-0 z-[60] pointer-events-none"
    >
      {/* Backdrop with cut-out for the highlighted element. We render four
          DIVs around the spotlight rect so the spotlight itself stays
          interactive (no overlay covering it). */}
      {rect ? (
        <>
          <div
            className="absolute bg-[#282C2A]/60 backdrop-blur-[2px] pointer-events-auto"
            style={{ top: 0, left: 0, right: 0, height: rect.top - 4 }}
            onClick={finish}
          />
          <div
            className="absolute bg-[#282C2A]/60 backdrop-blur-[2px] pointer-events-auto"
            style={{
              top: rect.top - 4,
              left: 0,
              width: rect.left - 4,
              height: rect.height + 8,
            }}
            onClick={finish}
          />
          <div
            className="absolute bg-[#282C2A]/60 backdrop-blur-[2px] pointer-events-auto"
            style={{
              top: rect.top - 4,
              left: rect.left + rect.width + 4,
              right: 0,
              height: rect.height + 8,
            }}
            onClick={finish}
          />
          <div
            className="absolute bg-[#282C2A]/60 backdrop-blur-[2px] pointer-events-auto"
            style={{
              top: rect.top + rect.height + 4,
              left: 0,
              right: 0,
              bottom: 0,
            }}
            onClick={finish}
          />
          {/* Outline ring around the spotlight */}
          <div
            className="absolute border-2 border-[#4A5D23] pointer-events-none shadow-[0_0_0_4px_rgba(74,93,35,0.25)]"
            style={{
              top: rect.top - 4,
              left: rect.left - 4,
              width: rect.width + 8,
              height: rect.height + 8,
            }}
          />
        </>
      ) : (
        <div
          className="absolute inset-0 bg-[#282C2A]/60 backdrop-blur-[2px] pointer-events-auto"
          onClick={finish}
        />
      )}

      {/* Tooltip card */}
      <div
        className="absolute bg-white border-2 border-[#282C2A] shadow-paper p-5 pointer-events-auto"
        style={tooltipStyle}
        data-testid="onboarding-tooltip"
      >
        <button
          type="button"
          onClick={finish}
          data-testid="onboarding-skip"
          className="absolute top-2 right-2 p-1 text-[#6C706D] hover:text-[#282C2A]"
          aria-label="Skip"
        >
          <X size={16} strokeWidth={1.5} />
        </button>
        <div className="label-eyebrow inline-flex items-center gap-2 text-[#4A5D23]">
          <Sparkles size={11} strokeWidth={1.75} />
          {lang === "en" ? "Quick start" : "Inizia subito"}
        </div>
        <h3 className="font-serif-display text-2xl text-[#282C2A] mt-2 leading-tight">
          {current.title}
        </h3>
        <p className="mt-2 text-sm text-[#6C706D] leading-relaxed">
          {current.body}
        </p>
        <div className="mt-5 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => (step + 1 < steps.length ? setStep(step + 1) : finish())}
            data-testid="onboarding-next"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#4A5D23] text-white text-xs font-semibold tracking-wider uppercase hover:bg-[#3B4A1C] transition-colors"
          >
            {step + 1 < steps.length
              ? lang === "en"
                ? "Next"
                : "Avanti"
              : lang === "en"
              ? "Got it"
              : "Ho capito"}
            <ArrowRight size={14} strokeWidth={1.75} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
